import React from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import dayjs from 'dayjs';

import {
    CellBadge,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import CellDate from 'src/registry/components/table/cells/CellDate';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import useSearch from 'src/registry/hooks/useSearch';
import usePagination from 'src/registry/hooks/usePagination';
import useSorting from 'src/registry/hooks/useSorting';
import useFilters from 'src/registry/hooks/useFilters';
import { generalMessages } from 'src/utils/messages';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';
import { ReferenceRate } from 'src/features/referenceRates/utils/types';
import { useFormatReferenceRateAmount } from 'src/features/referenceRates/hooks/useFormatReferenceRateAmount';
import { BILLING_STATUS_BADGE_OPTIONS } from 'src/features/servicePlanBillingPolicies/utils/constants';
import { useListReferenceRateValues } from 'src/features/referenceRateValues/api/useListReferenceRateValues';
import { referenceRateValuesMessages } from 'src/features/referenceRateValues/utils/messages';
import { ReferenceRateValue } from 'src/features/referenceRateValues/utils/types';
import CellReferenceRateValueActions from 'src/features/referenceRateValues/components/tables/CellReferenceRateValueActions';
import CreateReferenceRateValueFormModal from 'src/features/referenceRateValues/components/forms/CreateReferenceRateValueFormModal';

type ReferenceRateValuesTableProps = {
    /** The parent rate - values carry only an integer amount, in minor units of the rate's unit. */
    referenceRate?: ReferenceRate;
};

const ReferenceRateValuesTable = ({ referenceRate }: ReferenceRateValuesTableProps) => {
    const { formatMessage } = useIntl();
    const formatReferenceRateAmount = useFormatReferenceRateAmount();
    const { referenceRateId = '' } = useParams();
    const dateFormat = import.meta.env.VITE_DATE_FORMAT || 'DD.MM.YYYY';
    const [dateNow] = React.useState(() => Date.now());

    const permissionManageServicePlans = useHasPermissions([PERMISSION.MANAGE_SERVICE_PLANS]);

    const { querySearchOptions, ...search } = useSearch();
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySortingOptions, ...sorting } = useSorting({
        // Newest version first - the only editable rows are at the top.
        initialSortingState: [{ id: 'version_number', desc: true }],
    });

    const {
        data: referenceRateValues,
        isLoading,
        isError,
    } = useListReferenceRateValues({
        referenceRateId,
        ...querySearchOptions,
        ...queryPaginationOptions,
        ...queryFiltersOptions,
        ...querySortingOptions,
        config: { enabled: !!referenceRateId },
    });

    const columnHelper = createColumnHelper<ReferenceRateValue>();

    const columns = [
        columnHelper.accessor('version_number', {
            header: DataTableColumnHeader,
            enableSorting: true,
            meta: {
                title: formatMessage(referenceRateValuesMessages.version),
            },
            maxSize: 45,
        }),
        columnHelper.accessor('status', {
            header: DataTableColumnHeader,
            cell: CellBadge,
            meta: {
                title: formatMessage(generalMessages.status),
                badgeOptions: BILLING_STATUS_BADGE_OPTIONS,
            },
        }),
        columnHelper.accessor('valid_from', {
            enableSorting: true,
            header: DataTableColumnHeader,
            cell: CellDate,
            enableColumnFilter: true,
            meta: {
                title: formatMessage(generalMessages.validFrom),
                inputType: INPUT_TYPE.DATE,
                dateFilter: {
                    afterDate: new Date(dateNow - 365 * 24 * 60 * 60 * 1000),
                    beforeDate: new Date(),
                },
                inlineDateFilter: true,
            },
        }),
        // A null end date is the open-ended newest value, not missing data, so it gets a label
        // rather than CellDate's dash.
        columnHelper.accessor('valid_to', {
            enableSorting: true,
            header: DataTableColumnHeader,
            cell: ({ row }) =>
                row.original.valid_to ? (
                    <span className="whitespace-nowrap">{dayjs(row.original.valid_to).format(dateFormat)}</span>
                ) : (
                    <span className="whitespace-nowrap text-gray-500">
                        {formatMessage(referenceRateValuesMessages.openEnded)}
                    </span>
                ),
            enableColumnFilter: true,
            meta: {
                title: formatMessage(generalMessages.validTo),
                inputType: INPUT_TYPE.DATE,
                dateFilter: {
                    afterDate: new Date(dateNow - 365 * 24 * 60 * 60 * 1000),
                    beforeDate: new Date(),
                },
                inlineDateFilter: true,
            },
        }),
        columnHelper.accessor('amount', {
            enableSorting: true,
            header: DataTableColumnHeader,
            cell: ({ row }) => (
                <span className="whitespace-nowrap">
                    {formatReferenceRateAmount(row.original.amount, {
                        unit: referenceRate?.unit,
                        currency: referenceRate?.currency,
                    })}
                </span>
            ),
            meta: {
                title: formatMessage(referenceRateValuesMessages.amount),
            },
        }),
        columnHelper.accessor('source_note', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => row.original.source_note || '-',
            meta: {
                title: formatMessage(referenceRateValuesMessages.sourceNote),
                isLongText: true,
            },
        }),
        ...(permissionManageServicePlans
            ? [
                  columnHelper.display({
                      id: 'actions',
                      header: DataTableColumnHeader,
                      cell: CellReferenceRateValueActions,
                      maxSize: 45,
                  }),
              ]
            : []),
    ];

    return (
        <DataTable<ReferenceRateValue, unknown>
            columns={columns}
            data={referenceRateValues}
            semantics={{
                title: formatMessage(referenceRateValuesMessages.valueHistory),
                entryName: formatMessage(referenceRateValuesMessages.value).toLowerCase(),
                entryNames: formatMessage(referenceRateValuesMessages.values).toLowerCase(),
                placeholder: formatMessage(referenceRateValuesMessages.value).toLowerCase(),
            }}
            search={search}
            sorting={sorting}
            pagination={pagination}
            filters={filters}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(permissionManageServicePlans && { newEntry: CreateReferenceRateValueFormModal }),
            }}
        />
    );
};

export default ReferenceRateValuesTable;
